import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal, Button, Table, Form } from "react-bootstrap";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from "../../services/baseUrl";

const FAQs = () => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };

    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('add');
    const [formData, setFormData] = useState({ title: '', desc: '', order: 0, id: null });
    const [submitting, setSubmitting] = useState(false);

    const loadFaqs = useCallback(() => {
        setLoading(true);
        axios.get(`${BASE_URL}/faqs/all`, { headers })
            .then(res => setFaqs(res.data.faqs || []))
            .catch(() => toast.error("Failed to load FAQs."))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { loadFaqs(); }, [loadFaqs]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleOpenModal = (type, faq = null) => {
        setModalType(type);
        if (type === 'edit' && faq) {
            setFormData({ title: faq.title, desc: faq.desc, order: faq.order ?? 0, id: faq._id });
        } else {
            setFormData({ title: '', desc: '', order: 0, id: null });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (modalType === 'add') {
                await axios.post(`${BASE_URL}/faqs`, { title: formData.title, desc: formData.desc, order: formData.order }, { headers });
                toast.success("FAQ added successfully!");
            } else {
                await axios.put(`${BASE_URL}/faqs/${formData.id}`, { title: formData.title, desc: formData.desc, order: formData.order }, { headers });
                toast.success("FAQ updated successfully!");
            }
            loadFaqs();
            handleCloseModal();
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to ${modalType === 'add' ? 'add' : 'update'} FAQ.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (faq) => {
        try {
            await axios.put(`${BASE_URL}/faqs/${faq._id}`, { isActive: !faq.isActive }, { headers });
            toast.success(`FAQ ${faq.isActive ? 'deactivated' : 'activated'}.`);
            loadFaqs();
        } catch {
            toast.error("Failed to update FAQ status.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this FAQ?")) return;
        try {
            await axios.delete(`${BASE_URL}/faqs/${id}`, { headers });
            toast.success("FAQ deleted successfully!");
            loadFaqs();
        } catch {
            toast.error("Failed to delete FAQ.");
        }
    };

    return (
        <>
            <div className="container-fluid py-4 px-3 px-md-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold mb-0">FAQs</h2>
                        <p className="text-muted small mb-0">Manage Gorilla Fishing Room FAQs</p>
                    </div>
                    <Button onClick={() => handleOpenModal('add')} variant="primary">+ Add FAQ</Button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <Table striped bordered hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">#</th>
                                        <th>Title</th>
                                        <th>Description</th>
                                        <th>Order</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {faqs.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4 text-muted">No FAQs found. Add your first FAQ!</td>
                                        </tr>
                                    ) : faqs.map((faq, index) => (
                                        <tr key={faq._id}>
                                            <td className="ps-3">{index + 1}</td>
                                            <td className="fw-semibold">{faq.title}</td>
                                            <td className="text-muted" style={{ maxWidth: 400 }}>{faq.desc}</td>
                                            <td>{faq.order}</td>
                                            <td>
                                                <span className={`badge ${faq.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {faq.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    <Button size="sm" variant="outline-primary" onClick={() => handleOpenModal('edit', faq)}>Edit</Button>
                                                    <Button size="sm" variant={faq.isActive ? 'outline-secondary' : 'outline-success'} onClick={() => handleToggleActive(faq)}>
                                                        {faq.isActive ? 'Deactivate' : 'Activate'}
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(faq._id)}>Delete</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </div>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalType === 'add' ? 'Add FAQ' : 'Edit FAQ'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group controlId="formTitle" className="mb-3">
                            <Form.Label className="fw-semibold">Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter FAQ title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="formDesc" className="mb-3">
                            <Form.Label className="fw-semibold">Description <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter FAQ answer/description"
                                name="desc"
                                value={formData.desc}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="formOrder" className="mb-3">
                            <Form.Label className="fw-semibold">Display Order</Form.Label>
                            <Form.Control
                                type="number"
                                name="order"
                                value={formData.order}
                                onChange={handleInputChange}
                                min="0"
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</>
                                ) : (modalType === 'add' ? 'Add FAQ' : 'Update FAQ')}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default FAQs;

